import { isAxiosError } from "axios";
import { z } from "zod";

import { zodParseWithLog } from "./apiErrorLogs";

export type RemoteErrorStatus = "forbidden" | "not-found" | "contract-error" | "server-error";
export type RemoteCollectionErrorStatus = RemoteErrorStatus;

export type RemoteCollectionState<T> =
    | { status: "disabled" }
    | { status: "loading" }
    | { status: "success-value"; items: T[] }
    | { status: "empty"; items: T[] }
    | { status: RemoteCollectionErrorStatus };

export type RemoteCollectionErrorState = Extract<RemoteCollectionState<never>, { status: RemoteCollectionErrorStatus }>;

export type RemoteCollectionStateInput<T> = {
    data: T[] | undefined;
    enabled: boolean;
    error: unknown;
    isLoading: boolean;
};

const RemoteCollectionEnvelopeSchema = z.object({
    "hydra:member": z.unknown().optional(),
    member: z.unknown().optional(),
});

/** Parses a Hydra collection without treating a malformed envelope as an empty response. */
export const parseHydraCollectionMembers = <T>(data: unknown, memberSchema: z.ZodSchema<T>, operation: string): T[] => {
    const envelope = zodParseWithLog(data, RemoteCollectionEnvelopeSchema, `${operation}:collection-envelope`);
    const members = envelope["hydra:member"] ?? envelope.member;

    return zodParseWithLog(members, memberSchema.array(), operation);
};

/** Preserves transport and schema failures as explicit public states. */
export const getRemoteErrorStatus = (error: unknown): RemoteErrorStatus => {
    if (error instanceof z.ZodError) return "contract-error";

    if (isAxiosError(error)) {
        if (error.response?.status === 403) return "forbidden";
        if (error.response?.status === 404) return "not-found";
    }

    return "server-error";
};

export const isRetryableRemoteErrorStatus = (status: RemoteErrorStatus): boolean =>
    status === "contract-error" || status === "server-error";

/** Classifies remote list queries without coercing failures or unresolved data to an empty list. */
export const getRemoteCollectionState = <T>({
    data,
    enabled,
    error,
    isLoading,
}: RemoteCollectionStateInput<T>): RemoteCollectionState<T> => {
    if (!enabled) return { status: "disabled" };
    if (error !== null && error !== undefined) return { status: getRemoteErrorStatus(error) };
    if (isLoading || data === undefined) return { status: "loading" };
    if (data.length === 0) return { status: "empty", items: data };
    return { status: "success-value", items: data };
};

export const hasRemoteCollectionItems = <T>(
    state: RemoteCollectionState<T>,
): state is Extract<RemoteCollectionState<T>, { status: "success-value" | "empty" }> =>
    state.status === "success-value" || state.status === "empty";
